import { mountTabbedChart } from "./chart/tabbedChart.js";
import { mountAverageReturns } from "./chart/averageReturns.js";
import { renderBarChart } from "./chart/barChart.js";
import { mountHoldings } from "./chart/holdings.js";
import { renderHorizontalBarChart } from "./chart/horizontalBarChart.js";
import { renderLineChart } from "./chart/lineChart.js";
import { renderSplitHorizontalBarChart } from "./chart/splitHorizontalBarChart.js";
import { mountStatistics } from "./chart/statistics.js";
import {
  defaultBaseUrl,
  fetchJson,
  fetchSourcesCatalog,
  fetchWithClientOptions,
  enrichSourcesCatalog,
  findSource,
  joinUrl,
  resolveCatalogBaseUrl,
  clientFetchOptionsFrom,
} from "./client.js";
import { resolveDatasetUrl } from "./sourceAliases.js";
import {
  applyLineChartCatalogBounds,
  normalizeAverageReturns,
  normalizeBarChart,
  normalizeHoldings,
  normalizeHorizontalBarChart,
  normalizeLineChart,
  normalizeStatistics,
} from "./normalize.js";
import { isChartCompatible, makeIncompatibleDetail } from "./registry.js";
import { ensureChartFontsReady } from "./chart/fontsReady.js";
import { emptyStateCls } from "./chartClasses.js";
import type { NtenChartOptions, ChartHandle, NtenSingleChartOptions } from "./types.js";
import { isTabbedChartOptions } from "./types.js";

const DESTROY = "__nten_destroy" as const;
const DESTROY_LEGACY = "__an26_destroy" as const;

type WithDestroy = HTMLElement & {
  [DESTROY]?: () => void;
  [DESTROY_LEGACY]?: () => void;
};

function resolveTarget(target: string | HTMLElement): HTMLElement {
  if (typeof target === "string") {
    const el = document.querySelector(target);
    if (!el || !(el instanceof HTMLElement)) {
      throw new Error(`ntenChart: target not found for selector: ${target}`);
    }
    return el;
  }
  return target;
}

function destroyInContainer(el: HTMLElement): void {
  const w = el as WithDestroy;
  const destroy = w[DESTROY] ?? w[DESTROY_LEGACY];
  if (typeof destroy === "function") {
    destroy();
    delete w[DESTROY];
    delete w[DESTROY_LEGACY];
  }
  el.replaceChildren();
}

function setDestroy(w: WithDestroy, cleanup: () => void): void {
  w[DESTROY] = cleanup;
  w[DESTROY_LEGACY] = cleanup;
}

function renderIncompatibleMessage(el: HTMLElement, message: string): ChartHandle {
  el.innerHTML = `<div class="${emptyStateCls()}" style="padding:1rem;font-family:system-ui,sans-serif;font-size:14px;color:#5d6779;border:1px dashed #e7e8ec;border-radius:8px;">${message}</div>`;
  return {
    destroy() {
      el.replaceChildren();
    },
  };
}

async function mountSingleChart(options: NtenSingleChartOptions): Promise<ChartHandle> {
  const { type, source, options: userOpts } = options;
  const el = resolveTarget(options.target);
  const publishedBase = defaultBaseUrl();
  const dataBase = resolveCatalogBaseUrl(options.sourcesCatalogUrl);

  destroyInContainer(el);

  const catalogUrl = options.sourcesCatalogUrl ?? joinUrl(publishedBase, "sources.json");
  const fetchOptions = clientFetchOptionsFrom(options);

  let catalog = options.catalog;
  if (!catalog) {
    catalog = enrichSourcesCatalog(await fetchSourcesCatalog(catalogUrl, fetchOptions));
  } else {
    catalog = enrichSourcesCatalog(catalog);
  }

  const src = findSource(catalog, source);
  if (!src) {
    const msg = `Unknown source id "${source}".`;
    return renderIncompatibleMessage(el, msg);
  }

  const chartsUrl = options.chartsCatalogUrl ?? joinUrl(publishedBase, "charts.json");
  if (chartsUrl) {
    try {
      const res = await fetchWithClientOptions(chartsUrl, fetchOptions);
      if (res.ok) {
        const chartsDoc = (await res.json()) as { charts?: { id: string; allowedModes: string[] }[] };
        const chartDef = chartsDoc.charts?.find((c) => c.id === type);
        if (chartDef?.allowedModes?.length && !chartDef.allowedModes.includes(src.mode)) {
          console.warn(
            `nten-data: charts.json for chart type "${type}" does not list source mode "${src.mode}" in allowedModes. The SDK registry may disagree with the published catalog — verify deployment.`
          );
        }
      }
    } catch {
      /* optional catalog — ignore network errors */
    }
  }

  if (!isChartCompatible(type, src.mode)) {
    const detail = makeIncompatibleDetail(type, src.id, src.mode);
    const msg = `Source "${src.id}" uses mode ${src.mode}, which is not compatible with chart type "${type}". Allowed modes: ${detail.allowedModes.join(", ")}.`;
    console.warn(`nten-data: ${msg}`);
    userOpts?.onIncompatible?.(detail);
    return renderIncompatibleMessage(el, msg);
  }

  const dataUrl = joinUrl(dataBase, resolveDatasetUrl(src.url));
  const payload =
    options.dataOverride !== undefined
      ? options.dataOverride
      : await fetchJson<unknown>(dataUrl, fetchOptions);

  const title =
    userOpts?.title ??
    (typeof src.metadata?.title === "string" ? src.metadata.title : null) ??
    null;

  const subtitle = userOpts?.subtitle ?? null;
  const disclaimer = userOpts?.disclaimer ?? null;

  const w = el as WithDestroy;

  if (type === "bar_chart") {
    const data = normalizeBarChart(payload);
    const cleanup = renderBarChart(el, data, {
      title,
      subtitle,
      disclaimer: disclaimer ?? undefined,
    });
    setDestroy(w, cleanup);
  } else if (type === "line_chart") {
    const data = applyLineChartCatalogBounds(
      normalizeLineChart(payload),
      src.options as Record<string, unknown> | undefined
    );
    const cleanup = renderLineChart(el, data, {
      title,
      subtitle,
      disclaimer: disclaimer ?? undefined,
    });
    setDestroy(w, cleanup);
  } else if (type === "horizontal_bar_chart") {
    const data = normalizeHorizontalBarChart(payload);
    const cleanup = renderHorizontalBarChart(el, data, { title, subtitle });
    setDestroy(w, cleanup);
  } else if (type === "split_horizontal_bar") {
    const data = normalizeHorizontalBarChart(payload);
    const cleanup = renderSplitHorizontalBarChart(el, data, { title, subtitle });
    setDestroy(w, cleanup);
  } else if (type === "average_returns") {
    const data = normalizeAverageReturns(payload);
    setDestroy(w, mountAverageReturns(el, data, { title, disclaimer: disclaimer ?? undefined }));
  } else if (type === "statistics") {
    const data = normalizeStatistics(payload);
    setDestroy(
      w,
      mountStatistics(el, data, {
        title,
        disclaimer: disclaimer ?? userOpts?.legalText ?? undefined,
      })
    );
  } else if (type === "holdings") {
    const data = normalizeHoldings(payload);
    const pdfUrl =
      src.metadata?.pdfStatus === "ready" && typeof src.metadata?.pdfUrl === "string"
        ? joinUrl(dataBase, src.metadata.pdfUrl)
        : null;
    setDestroy(w, mountHoldings(el, data, { title, subtitle, disclaimer: disclaimer ?? undefined, pdfUrl }));
  } else {
    throw new Error(`Unsupported chart type: ${type as string}`);
  }

  return {
    destroy() {
      destroyInContainer(el);
    },
  };
}

/**
 * Load the sources catalog, fetch the series for `source`, validate mode vs `type`,
 * and render a chart into `target`. For `type: "tabbed"`, renders a tabbed Performance shell.
 */
export async function ntenChart(options: NtenChartOptions): Promise<ChartHandle> {
  // Gate every render path on brand fonts so measured text — and therefore chart
  // geometry — is deterministic. See ./chart/fontsReady.ts. Tabbed panes mounted
  // later inherit this: the fonts are already resolved by the time tabs switch.
  await ensureChartFontsReady();

  if (isTabbedChartOptions(options)) {
    const el = resolveTarget(options.target);
    const publishedBase = defaultBaseUrl();
    const dataBase = resolveCatalogBaseUrl(options.sourcesCatalogUrl);
    destroyInContainer(el);

    const catalogUrl = options.sourcesCatalogUrl ?? joinUrl(publishedBase, "sources.json");
    const fetchOptions = clientFetchOptionsFrom(options);

    let catalog = options.catalog;
    if (!catalog) {
      catalog = enrichSourcesCatalog(await fetchSourcesCatalog(catalogUrl, fetchOptions));
    } else {
      catalog = enrichSourcesCatalog(catalog);
    }

    const handle = await mountTabbedChart({
      container: el,
      options,
      catalog,
      dataBase,
      fetchOptions,
    });
    const w = el as WithDestroy;
    setDestroy(w, () => handle.destroy());
    return handle;
  }

  return mountSingleChart(options);
}
